import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type Acao5W2H = Database['public']['Tables']['acoes_5w2h']['Row'];
type Acao5W2HInsert = Database['public']['Tables']['acoes_5w2h']['Insert'];
type Acao5W2HUpdate = Database['public']['Tables']['acoes_5w2h']['Update'];

export function useAcoes5W2H(planoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['acoes-5w2h', planoAcaoId],
    queryFn: async () => {
      if (!planoAcaoId) return [];
      const { data, error } = await supabase
        .from('acoes_5w2h')
        .select(`
          *,
          responsavel:users!acoes_5w2h_who_quem_fkey(id, name)
        `)
        .eq('plano_acao_id', planoAcaoId)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!planoAcaoId
  });

  const createMutation = useMutation({
    mutationFn: async (data: Acao5W2HInsert) => {
      // Obter próxima ordem
      const { data: existing } = await supabase
        .from('acoes_5w2h')
        .select('ordem')
        .eq('plano_acao_id', data.plano_acao_id)
        .order('ordem', { ascending: false })
        .limit(1);
      
      const nextOrdem = (existing?.[0]?.ordem ?? 0) + 1;

      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .insert({ ...data, ordem: nextOrdem })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h', planoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['planos-acao-hibrido'] });
      toast({ title: 'Ação criada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao criar ação', description: error.message, variant: 'destructive' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Acao5W2HUpdate & { id: string }) => {
      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h', planoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['planos-acao-hibrido'] });
      toast({ title: 'Ação atualizada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar ação', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('acoes_5w2h').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h', planoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['planos-acao-hibrido'] });
      toast({ title: 'Ação excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir ação', description: error.message, variant: 'destructive' });
    }
  });

  // Atualizar status (para Kanban)
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updateData: Acao5W2HUpdate = { status };
      
      // Se concluída, registrar data
      if (status === 'concluida') {
        updateData.data_conclusao_real = new Date().toISOString();
        updateData.percentual_conclusao = 100;
      } else if (status === 'em_andamento') {
        updateData.data_inicio_real = new Date().toISOString();
      }

      const { data: result, error } = await supabase
        .from('acoes_5w2h')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h', planoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['planos-acao-hibrido'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar status', description: error.message, variant: 'destructive' });
    }
  });

  // Reordenar ações
  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => 
        supabase.from('acoes_5w2h').update({ ordem: index + 1 }).eq('id', id)
      );
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes-5w2h', planoAcaoId] });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao reordenar', description: error.message, variant: 'destructive' });
    }
  });

  return {
    acoes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createAcao: createMutation.mutate,
    updateAcao: updateMutation.mutate,
    deleteAcao: deleteMutation.mutate,
    updateStatus: updateStatusMutation.mutate,
    reorderAcoes: reorderMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
